import * as THREE from 'three';
import { drawn } from './patterns.js';
import { Parts } from './parts.js';

/**
 * A Dutch polder mill in code (part 2's polder, 29 Sep 2026): a thatched
 * octagonal body on a brick foot, a turning cap, and four sails of lattice
 * on white stocks. The sails face `towards` (the stage) and turn about their
 * axle; `update` eases their speed with a feature's level. One mesh per
 * material, and one for the sails' lattice.
 */

export const MILL = {
  foot: 2.6,
  body: 13,
  /** The sails' reach from the axle. */
  sail: 12,
} as const;

/** Lattice for a sail: two rails and cross bars, clear between, cut out. */
export function latticeTexture(width = 32, height = 256): THREE.DataTexture {
  return drawn(
    width,
    height,
    (data) => {
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const rail = x < 3 || x >= width - 3;
          const bar = y % 16 < 2;
          const mid = Math.abs(x - width / 2) < 1;
          if (!rail && !bar && !mid) continue;
          const shade = rail ? 0.36 : 0.5;
          data.set(
            [
              Math.round(shade * 255),
              Math.round(shade * 0.6 * 255),
              Math.round(shade * 0.5 * 255),
              255,
            ],
            (y * width + x) * 4,
          );
        }
      }
    },
    { repeat: false },
  );
}

/** Sails coming up to speed slowly (the windmill's, 28 Sep 2026): the speed follows its target with a lag. */
export function spinner(options: { lag: number; radiansPerSecond: number }): {
  readonly angle: number;
  update(dt: number, target: number): void;
  snap(target: number): void;
} {
  let speedNow = 0;
  let angle = 0;
  return {
    get angle() {
      return angle;
    },
    update(dt, target) {
      speedNow += (target - speedNow) * Math.min(1, options.lag > 0 ? dt / options.lag : 1);
      angle += dt * options.radiansPerSecond * speedNow;
    },
    snap(target) {
      speedNow = target;
    },
  };
}

export type Windmill = {
  readonly group: THREE.Group;
  /** The sails, turning about their axle (local z). */
  readonly sails: THREE.Group;
  /** How fast they turn, 0–1 of full speed: a feature's level. */
  update(dt: number, speed: number): void;
  /** At that speed at once: a lesson jumped to a state rather than moving on by one. */
  snap(speed: number): void;
};

export function buildWindmill(options: {
  readonly at: readonly [number, number, number];
  /** The point the sails face (the stage). */
  readonly towards: readonly [number, number];
  readonly thatch: THREE.Texture;
  readonly lattice: THREE.Texture;
  readonly name: string;
}): Windmill {
  const group = new THREE.Group();
  group.name = options.name;
  group.position.set(...options.at);
  // Turned so its local +z (the sails' side) faces the stage.
  group.rotation.y = Math.atan2(
    options.towards[0] - options.at[0],
    options.towards[1] - options.at[2],
  );

  const parts = new Parts();
  const top = MILL.foot + MILL.body;
  parts.cylinder('brick', 4.9, MILL.foot, [0, MILL.foot / 2, 0], 0x8b4c38, 8, 4.7);
  parts.add(
    'thatch',
    new THREE.CylinderGeometry(2.55, 4.5, MILL.body, 8, 1, false),
    new THREE.Matrix4().makeTranslation(0, MILL.foot + MILL.body / 2, 0),
    0xffffff,
  );
  // Doors on the stage's side and the far side, a small window above.
  for (const side of [1, -1]) {
    parts.boxAt(
      'wood',
      [1.2, 2.1, 0.3],
      new THREE.Matrix4().makeTranslation(0, 1.05, side * 4.75),
      0x2e3a2c,
    );
  }
  parts.boxAt(
    'wood',
    [0.7, 0.9, 0.3],
    new THREE.Matrix4().makeTranslation(0, MILL.foot + 7, 3.35),
    0x2e3a2c,
  );
  // The cap: a rounded hood lying along the axle, a little forward.
  const cap = new THREE.CylinderGeometry(2.7, 2.7, 5.6, 12, 1, false, 0, Math.PI);
  cap.rotateZ(Math.PI / 2);
  cap.rotateY(Math.PI / 2);
  parts.add('cap', cap, new THREE.Matrix4().makeTranslation(0, top, 0.4), 0x3d3832);
  parts.boxAt(
    'cap',
    [5.4, 0.3, 5.8],
    new THREE.Matrix4().makeTranslation(0, top + 0.1, 0.4),
    0x3d3832,
  );
  // The axle, tilted up a little, out of the cap's front.
  const axle = new THREE.Vector3(0, top + 1.3, 3.6);
  parts.add(
    'wood',
    new THREE.CylinderGeometry(0.35, 0.35, 1.8, 8).rotateX(Math.PI / 2 - 0.12),
    new THREE.Matrix4().makeTranslation(axle.x, axle.y, axle.z - 0.7),
    0x3a3530,
  );

  const materials = {
    brick: new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9 }),
    thatch: new THREE.MeshStandardMaterial({ map: options.thatch, color: 0x7a6e58, roughness: 1 }),
    wood: new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.8 }),
    cap: new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85 }),
    stock: new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.7 }),
  };
  for (const mesh of parts.meshes(materials)) {
    mesh.name = `${options.name}-${mesh.name}`;
    group.add(mesh);
  }

  // The sails: four stocks and their lattice, on a hub at the axle's end.
  const sails = new THREE.Group();
  sails.name = `${options.name}-sails`;
  sails.position.copy(axle);
  sails.rotation.x = -0.12;
  const stocks = new Parts();
  for (let k = 0; k < 2; k++) {
    stocks.boxAt(
      'stock',
      [0.34, MILL.sail * 2, 0.34],
      new THREE.Matrix4().makeRotationZ((k * Math.PI) / 2),
      0xe9e4da,
    );
  }
  // The hub: a disc on the axle, in the sails' plane.
  stocks.add(
    'stock',
    new THREE.CylinderGeometry(0.6, 0.6, 0.5, 10).rotateX(Math.PI / 2),
    new THREE.Matrix4(),
    0x3a3530,
  );
  for (const mesh of stocks.meshes(materials)) {
    mesh.name = `${options.name}-stocks`;
    sails.add(mesh);
  }
  // Each sail's lattice beside its stock, from 1.8 m out to its tip, the same way round.
  const card = new THREE.PlaneGeometry(2.1, MILL.sail - 1.8).translate(
    1.2,
    1.8 + (MILL.sail - 1.8) / 2,
    0.2,
  );
  const cards: THREE.BufferGeometry[] = [];
  for (let k = 0; k < 4; k++) cards.push(card.clone().rotateZ((k * Math.PI) / 2));
  const lattice = new THREE.Mesh(
    mergeCards(cards),
    new THREE.MeshStandardMaterial({
      map: options.lattice,
      alphaTest: 0.5,
      side: THREE.DoubleSide,
      roughness: 0.8,
    }),
  );
  lattice.material.alphaToCoverage = true;
  lattice.name = `${options.name}-lattice`;
  sails.add(lattice);
  group.add(sails);

  const spin = spinner({ lag: 1.25, radiansPerSecond: 1.1 });
  return {
    group,
    sails,
    update(dt, speed) {
      // Mills come up to speed slowly: the sails follow the level with a lag.
      spin.update(dt, speed);
      sails.rotation.z = -spin.angle;
    },
    snap(speed) {
      spin.snap(speed);
    },
  };
}

function mergeCards(cards: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const index: number[] = [];
  for (const card of cards) {
    const base = positions.length / 3;
    positions.push(...card.getAttribute('position').array);
    normals.push(...card.getAttribute('normal').array);
    uvs.push(...card.getAttribute('uv').array);
    for (const i of card.index!.array) index.push(base + i);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setIndex(index);
  return geometry;
}
